import _map from 'lodash/map';
import _without from 'lodash/without';
import _reduce from 'lodash/reduce';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';

import { createReducer } from 'redux-act';
import Immutable from 'seamless-immutable';
import PageActions from './page-actions';

const initialState = Immutable({
  byId:    {},
  byPhase: {}, // each key is the phase, each value is an array of page ids

  isFetching: false,

  //props for saving a page
  isSaving:      false,
  isSaveSuccess: false,
  isSaveError:   false,
  emptyPhases:   [],
});

const reducer = createReducer((on) => {
  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Fetching pages
  on(PageActions.fetchPages, (state) => {
    return state.set('isFetching', true);
  });

  on(PageActions.fetchPagesFailure, (state) => {
    return state.set('isFetching', false);
  });

  // Note that we need to record the order in which they are returned so we can acurately render phases in the experience page section
  on(PageActions.fetchPagesSuccess, (state, { pages }) => {
    const byId = {};
    const byPhase = {};

    _map(pages, (page) => {
      // putting all the pages in by id
      byId[page.id] = page;

      // adding a byPhase attribute to take the page ids for each phase
      if (byPhase.hasOwnProperty(page.phaseInfo.label)) {
        byPhase[page.phaseInfo.label].push(page.id);
      } else byPhase[page.phaseInfo.label] = [page.id];
    });

    return state.set('isFetching', false).set('byId', byId).set('byPhase', byPhase);
  });

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Persisting change
  on(PageActions.savePages, (state) => state.set('isSaving', true));

  on(PageActions.updatePage, (state, { page }) => {
    // eager update on save prevents visual issues for the user
    const updatedPage = {
      byId: {
        [page.id]: page,
      },
    };
    return state.merge(updatedPage, { deep: true });
  });

  on(PageActions.updatePageSuccess, (state) => {
    // on success update statuses
    return state.set('isSaving', false).set('isSaveSuccess', true);
  });

  on(PageActions.updatePageFailure, (state, { page }) => {
    // on failure, revert the page
    const updatedPage = {
      byId: {
        [page.id]: page,
      },
    };
    return state.set('isSaving', false).set('isSaveError', true).merge(updatedPage, { deep: true });
  });

  on(PageActions.savePagesSuccess, (state) => state.merge({ isSaving: false, isSaveSuccess: true }));
  on(PageActions.savePagesFailure, (state) => state.merge({ isSaving: false, isSaveError: true }));

  on(PageActions.finishSaving, (state) => state.merge({ isSaving: false, isSaveSuccess: false, isSaveError: false }));

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Updating page entity
  on(PageActions.receiveUpdatedPage, (state, { page }) => {
    const updatedPage = {
      byId: {
        [page.id]: page,
      },
    };
    return state.merge(updatedPage, { deep: true });
  });

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Deleting pages
  on(PageActions.deletePageSuccess, (state, { id }) => {
    const _byId = state.byId.without(id);
    return state.set('byId', _byId);
  });

  // When a page is deleted from the page entitiy, we need to reevaluate what pages are in each phase
  on(PageActions.deletePage, (state, { id }) => {
    const _byPhase = state.byPhase;

    const _newByPhase = _reduce(
      _byPhase,
      (newPageOrder, pages, phase) => {
        if (_includes(pages, id)) {
          // if the passed in page is in this phase, remove it from the phase
          const _pages = _without(_byPhase[phase], id);
          // ...but only leave the phase in place if there's still > 1 page in the phase
          if (!_isEmpty(_pages)) newPageOrder[phase] = _pages;
        } else newPageOrder[phase] = pages;

        return newPageOrder;
      },
      {}
    );

    return state.set('byPhase', _newByPhase);
  });

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  on(PageActions.setByPhase, (state, { byPhase }) => state.set('byPhase', byPhase));

  on(PageActions.changePageStatus, (state, { id, status }) => {
    return state.setIn(['byId', id, 'status'], status);
  });

  on(PageActions.fetchEmptyPhasesSuccess, (state, { emptyPhases }) => {
    return state.set('emptyPhases', emptyPhases);
  });
}, initialState);

export default reducer;
